// pages/AllCategoriesPage.js – Display ALL categories and subcategories from Firebase
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProducts } from '../hooks/useProducts';
import { dbGet } from '../config/firebase';
import { ArrowLeft, Search, X, ChevronRight, Flower, Package } from 'lucide-react';
import { toast } from 'react-toastify';

const AllCategoriesPage = () => {
  const navigate = useNavigate();
  const { products: allProducts, loading: productsLoading } = useProducts();
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch categories and subcategories from Firebase
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        let categoriesData = await dbGet('shop_categories');
        let categoriesArray = [];
        
        if (categoriesData && Array.isArray(categoriesData)) {
          categoriesArray = categoriesData;
        } else if (categoriesData && typeof categoriesData === 'object') {
          categoriesArray = Object.values(categoriesData);
        }
        
        // If no categories in Firebase, use default categories
        if (categoriesArray.length === 0) {
          categoriesArray = [
            { id: 'bouquets', name: 'Bouquets', icon: '💐' },
            { id: 'fresh-flowers', name: 'Fresh Flowers', icon: '🌸' },
            { id: 'gift-hampers', name: 'Gift Hampers', icon: '🎁' },
            { id: 'plants', name: 'Plants', icon: '🪴' },
            { id: 'cakes', name: 'Cakes', icon: '🍰' },
            { id: 'chocolates', name: 'Chocolates', icon: '🍫' },
            { id: 'decor', name: 'Home Decor', icon: '🏠' },
            { id: 'combos', name: 'Combo Offers', icon: '🎉' }
          ];
        }
        
        setCategories(categoriesArray);
        
        // Fetch subcategories
        let subcategoriesData = await dbGet('shop_subcategories');
        let subcategoriesObj = {};
        
        if (subcategoriesData && typeof subcategoriesData === 'object') {
          subcategoriesObj = subcategoriesData;
        }
        
        setSubcategories(subcategoriesObj);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setCategories([
          { id: 'bouquets', name: 'Bouquets', icon: '💐' },
          { id: 'fresh-flowers', name: 'Fresh Flowers', icon: '🌸' },
          { id: 'gift-hampers', name: 'Gift Hampers', icon: '🎁' },
          { id: 'plants', name: 'Plants', icon: '🪴' }
        ]);
        setSubcategories({});
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Get all products as array
  const productsArray = useMemo(() => {
    if (!allProducts) return [];
    if (Array.isArray(allProducts)) return allProducts;
    if (typeof allProducts === 'object') {
      const flatArray = [];
      Object.values(allProducts).forEach(category => {
        if (Array.isArray(category)) {
          flatArray.push(...category);
        }
      });
      return flatArray;
    }
    return [];
  }, [allProducts]);

  // Filter only in-stock products
  const inStockProducts = useMemo(() => {
    return productsArray.filter(p => p && p.stock !== false && p.outOfStock !== true);
  }, [productsArray]);

  // Get ALL categories with product counts
  const allCategoriesWithDetails = useMemo(() => {
    return categories.map(category => {
      const categoryProducts = inStockProducts.filter(product => {
        const matchesById = product.category === category.id || 
                           product.mainCategory === category.id ||
                           product.subcategoryId === category.id;
        const matchesByName = product.category === category.name ||
                             product.mainCategory === category.name;
        return matchesById || matchesByName;
      });
      
      const categoryImage = categoryProducts.length > 0 ? categoryProducts[0]?.image : null;
      
      return {
        id: category.id,
        name: category.name,
        icon: category.icon || '🌸',
        count: categoryProducts.length,
        image: categoryImage,
        products: categoryProducts,
        hasProducts: categoryProducts.length > 0
      };
    });
  }, [categories, inStockProducts]);

  // Get ALL subcategories with product counts
  const allSubcategoriesWithDetails = useMemo(() => {
    const subcategoryList = [];
    
    Object.entries(subcategories).forEach(([categoryId, subs]) => {
      if (Array.isArray(subs)) {
        subs.forEach(sub => {
          if (sub && typeof sub === 'object') {
            const subProducts = inStockProducts.filter(product => 
              product.subcategoryId === sub.id || 
              product.subcategory === sub.name ||
              product.subcategory === sub.id
            );
            
            const subImage = subProducts.length > 0 ? subProducts[0]?.image : null;
            
            // Find parent category name
            const parentCategory = categories.find(c => c.id === categoryId);
            
            subcategoryList.push({
              id: sub.id,
              name: sub.name,
              categoryId: categoryId,
              parentCategoryName: parentCategory?.name || '',
              count: subProducts.length,
              image: subImage,
              products: subProducts,
              hasProducts: subProducts.length > 0
            });
          }
        });
      }
    });
    
    return subcategoryList;
  }, [subcategories, inStockProducts, categories]);

  // Combine all items (both categories and subcategories)
  const allItems = useMemo(() => {
    return [...allCategoriesWithDetails, ...allSubcategoriesWithDetails];
  }, [allCategoriesWithDetails, allSubcategoriesWithDetails]);

  // Filter items based on search
  const displayedItems = useMemo(() => {
    if (!searchTerm.trim()) return allItems;
    const query = searchTerm.toLowerCase();
    return allItems.filter(item => item.name.toLowerCase().includes(query));
  }, [allItems, searchTerm]);

  const handleItemClick = (item) => {
    // Check if it's a category or subcategory
    const isCategory = allCategoriesWithDetails.some(cat => cat.id === item.id);
    
    if (isCategory) {
      navigate(`/category/${item.id}`, { 
        state: { 
          category: { 
            id: item.id, 
            name: item.name, 
            icon: item.icon 
          } 
        } 
      });
    } else {
      // Find the parent category for subcategory
      const parentCategory = categories.find(c => c.id === item.categoryId);
      if (parentCategory) {
        navigate(`/category/${parentCategory.id}`, { 
          state: { 
            category: parentCategory,
            selectedSubcategory: { id: item.id, name: item.name }
          } 
        });
      } else {
        toast.info(`Category not found`);
      }
    }
  };

  const handleProductClick = (e, productId) => {
    e.stopPropagation();
    navigate(`/product/${productId}`);
  };

  if (loading || productsLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div className="spinner" style={{ width: 40, height: 40, border: '3px solid #dc2626', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
      </div>
    );
  }

  const getCountText = (count) => {
    if (count === 0) return 'No products yet';
    if (count === 1) return '1 product';
    return `${count} products`;
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', paddingBottom: '50px' }}>
      {/* Header - Professional Cherry Theme */}
      <div style={{ 
        background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
        padding: isMobile ? '12px 16px' : '16px 24px', 
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <button 
            onClick={() => navigate(-1)} 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px', 
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              padding: '8px 16px', 
              borderRadius: '8px', 
              cursor: 'pointer',
              fontSize: '0.85rem',
              color: 'white',
              fontWeight: 500
            }}
          >
            <ArrowLeft size={16} /> Back
          </button>
          
          <h1 style={{ fontSize: isMobile ? '1.2rem' : '1.5rem', fontWeight: 700, margin: 0, color: 'white', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Flower size={24} color="white" />
            Shop Categories
          </h1>
          
         
        </div>
      </div>

      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '24px 16px' }}>
        {/* Results Count */}
        <div style={{ marginBottom: '24px' }}>
          <p style={{ color: '#64748b', fontSize: '0.85rem' ,fontWeight:'900'}}>
            Showing Best of All Arrivals
            
          </p>
        </div>

        {/* Items Grid */}
        {displayedItems.length > 0 ? (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', 
            gap: isMobile ? '16px' : '24px' 
          }}>
            {displayedItems.map((item, idx) => (
              <div 
                key={item.id || idx}
                onClick={() => handleItemClick(item)}
                style={{ 
                  background: 'white', 
                  borderRadius: '16px', 
                  overflow: 'hidden', 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  border: '1px solid #e2e8f0',
                  opacity: item.hasProducts ? 1 : 0.7
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.1)';
                  e.currentTarget.style.borderColor = '#dc2626';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }}
              >
                {/* Item Image */}
                <div style={{ 
                  width: '100%',
                  height: isMobile ? '160px' : '200px',
                  overflow: 'hidden',
                  background: '#f8fafc',
                  position: 'relative'
                }}>
                  {item.image ? (
                    <img 
                      src={item.image} 
                      alt={item.name}
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover',
                        transition: 'transform 0.5s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (item.hasProducts) e.currentTarget.style.transform = 'scale(1.08)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=400&q=80';
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'linear-gradient(135deg, #fff5f5, #fff)'
                    }}>
                      <span style={{ fontSize: isMobile ? '48px' : '64px', marginBottom: '8px' }}>
                        {item.icon || '🌸'}
                      </span>
                      {!item.hasProducts && (
                        <span style={{ fontSize: '11px', color: '#94a3b8' }}>Coming Soon</span>
                      )}
                    </div>
                  )}
                  
                  {/* No Products Overlay */}
                  {!item.hasProducts && (
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                      padding: '20px 12px 12px'
                    }}>
                      <span style={{ color: 'white', fontSize: '11px', fontWeight: 500 }}>
                        Coming Soon
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Content - Below Image */}
                <div style={{ padding: '16px' }}>
                  <h3 style={{ 
                    fontSize: isMobile ? '0.9rem' : '1rem', 
                    fontWeight: 600, 
                    margin: 0, 
                    color: '#1e293b',
                    marginBottom: '6px',
                    lineHeight: 1.3
                  }}>
                    {item.name}
                  </h3>
                  
                  <p style={{ 
                    fontSize: '0.7rem', 
                    color: item.hasProducts ? '#dc2626' : '#94a3b8', 
                    fontWeight: 500,
                    marginBottom: '12px'
                  }}>
                    {getCountText(item.count)}
                  </p>
                  
                  {/* Product Preview Row - Only show if products exist */}
                 
                  
                  {/* Shop Now Link */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderTop: '1px solid #f1f5f9',
                    paddingTop: '12px',
                    marginTop: item.hasProducts && item.products?.length > 0 ? '0' : '8px'
                  }}>
                    <span style={{ 
                      fontSize: '0.7rem', 
                      color: item.hasProducts ? '#dc2626' : '#94a3b8', 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '6px',
                      fontWeight: 600,
                      transition: 'gap 0.2s'
                    }}>
                      {item.hasProducts ? 'Shop Now' : 'Coming Soon'} <ChevronRight size={12} />
                    </span>
                    <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                      {item.hasProducts ? 'Browse' : 'Check back'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px', background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
            <Package size={48} color="#cbd5e1" />
            <h3 style={{ fontSize: '1rem', marginTop: '16px', color: '#1e293b' }}>No items found</h3>
            <p style={{ color: '#64748b', marginTop: '8px' }}>
              {searchTerm ? `No items matching "${searchTerm}"` : 'Add categories or subcategories to see them here'}
            </p>
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')} 
                style={{ 
                  marginTop: '20px', 
                  padding: '10px 24px', 
                  background: '#dc2626', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  cursor: 'pointer', 
                  fontWeight: 600 
                }}
              >
                Clear Search
              </button>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .spinner {
          animation: spin 0.6s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default AllCategoriesPage;